import React from 'react';

import { store } from 'Models/Store';
import { EffectiveCargoCharacteristicDto } from 'Modules/Cargo';
import { Button, Colors } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import CargoCharacteristicTags from 'Views/Components/_HumanWritten/Tag/CargoCharacteristicTags';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import alertToast from 'Util/ToastifyUtils';
import { CustomerCargoProfileDto, deleteCargoProfile } from 'Services/Api/_HumanWritten/CustomerCargoProfileService';

/**
 * The profile's effective characteristics = (inherited \ removed) ∪ added — what actually gets applied
 * to a booking. `source` marks whether each came from the cargo type (inherited) or the profile (added).
 */
function effectiveCharacteristics(profile: CustomerCargoProfileDto): EffectiveCargoCharacteristicDto[] {
	const inherited = store.cargoTypeCharacteristicsMap[profile.cargoTypeId] ?? [];
	const removed = new Set(profile.removedInheritedCharacteristicIds);
	const added = new Set(profile.addedCharacteristicIds);
	const effectiveIds = [...new Set([...inherited.filter(id => !removed.has(id)), ...profile.addedCharacteristicIds])];
	return effectiveIds.map(id => ({
		id,
		name: store.cargoCharacteristicMap[id]?.name ?? '',
		source: added.has(id) ? 'Selected' : 'Inherited',
	}));
}

export interface CargoProfileCardProps {
	profile: CustomerCargoProfileDto;
	/** "{make} {model}" of the profile's cargo type. */
	cargoTypeLabel: string;
	onEdit: () => void;
	onRemove: () => void;
}

export function CargoProfileCard({ profile, cargoTypeLabel, onEdit, onRemove }: CargoProfileCardProps) {
	const handleRemove = async () => {
		let confirmed = false;
		try {
			confirmed = await newConfirmModal(
				'Remove cargo profile',
				<p>Are you sure you want to remove the &quot;{cargoTypeLabel}&quot; cargo profile?</p>,
			);
		} catch {
			// User cancelled
			return;
		}

		if (!confirmed) {
			return;
		}

		try {
			await deleteCargoProfile(profile.id);
			alertToast('Cargo profile removed', 'success', 'Cargo profile removed');
			onRemove();
		} catch (error) {
			alertToast('Failed to remove cargo profile', 'error', 'Error');
		}
	};

	const effective = effectiveCharacteristics(profile);

	return (
		<div className="vehicle-card" data-test-id="cargo-profile-card">
			<div className="vehicle-card__info">
				<p className="vehicle-card__make-model">{cargoTypeLabel}</p>
				{effective.length > 0 && (
					<Flex gap="xxs" flexWrap="wrap" align="center" className="mt-1">
						<CargoCharacteristicTags
							effectiveCharacteristics={effective}
							cargoCharacteristicMap={store.cargoCharacteristicMap}
						/>
					</Flex>
				)}
			</div>
			{/* A profile changes what the customer is charged, so only manager and above can change
			  * one — staff can see the profiles but not edit or remove them. The endpoints enforce
			  * the same. */}
			{store.isManager && (
				<div className="vehicle-card__actions">
					<Button
						className="btn--text"
						colors={Colors.Secondary}
						onClick={onEdit}
						buttonProps={{ 'data-test-id': 'cargo-profile-edit-button' } as any}
					>
						Edit
					</Button>
					<Button
						className="btn--text"
						colors={Colors.Red}
						onClick={handleRemove}
						buttonProps={{ 'data-test-id': 'cargo-profile-remove-button' } as any}
					>
						Delete
					</Button>
				</div>
			)}
		</div>
	);
}
